import React from 'react';
import ReactDom from 'react-dom';
import _ from 'lodash';
import Item from './CaptionContainerItem';
import styles from './css/CaptionContainer.styl';
import {FormControls} from '../../../../shared/library';
import {
  bindMethods,
  setRef,
} from '../../../../shared/utilities/componentHelpers';

const {Button} = FormControls;

export default class CaptionContainer extends React.Component {
  static propTypes = {
    handleMediaCollection: React.PropTypes.func,
    isInitialUpload: React.PropTypes.bool,
    mediaCollection: React.PropTypes.array,
    mobileDetails: React.PropTypes.object.isRequired,
    scrollPosition: React.PropTypes.number,
  };

  /**
   * CaptionContainer Component constructor
   * @param  {Object}  props - props object
   * @return {Undefined} Returns nothing
   */
  constructor(props) {
    super();

    this.state = {
      removalCount: 0,
      bodyHeight: 0,
      footerTop: 0,
      itemHeight: 0,
      windowHeight: 0,
    };

    this.scrollPosition = null;
    this.collectionSize = _.size(props.mediaCollection);

    this.mediaType = _.get(props.mediaCollection, '[0].mediaType');
    this.mediaUpdates = {};

    this.bodyRef = null;
    this.footerRef = null;
    this.itemRef = null;
    this.setBodyRef = _.partial(setRef, _, 'bodyRef', this);
    this.setFooterRef = _.partial(setRef, _, 'footerRef', this);
    this.setItemRef = _.partial(setRef, _, 'itemRef', this);

    bindMethods(this, [
      'handleCancel',
      'handleSubmit',
      'handleItemChange',
      'handleOrientationChange',
    ]);
  }

  /**
   * Lifecycle method
   * @returns {Undefined} Returns nothing
   */
  componentDidMount() {
    this.scrollPosition =
      _.get(this.props, 'scrollPosition') || window.pageYOffset;
    if (this.props.mobileDetails.any) {
      this.handleOrientationChange();
    }
    window.addEventListener('orientationchange', this.handleOrientationChange);
  }

  /**
   * Lifecycle method
   * @returns {Undefined} Returns nothing
   */
  componentWillUnmount() {
    window.removeEventListener(
      'orientationchange',
      this.handleOrientationChange
    );
  }

  /**
   * Sets various styles used for mobile layout
   * @returns {Undefined} Returns nothing
   */
  setStyles() {
    const windowHeight = window.innerHeight;
    const footerHeight = _.get(this.footerRef, 'clientHeight', 0);
    const bodyOffset = _.get(this.bodyRef, 'offsetTop', 0);
    const bodyHeight = windowHeight - bodyOffset;
    const footerTop = windowHeight - footerHeight;
    const itemHeight = _.get(
      ReactDom.findDOMNode(this.itemRef),
      'clientWidth',
      0
    );

    this.setState({
      bodyHeight,
      footerTop,
      itemHeight,
      windowHeight,
    });
  }

  /**
   * Cancels media changes
   * @param  {Object} mediaCollection           Object containing uploaded media
   * @param  {Boolean} isInitialUpload          Is this the initial upload
   * @return {Undefined} Returns nothing
   */
  handleCancel(mediaCollection, isInitialUpload) {
    const collection = isInitialUpload ? [] : mediaCollection;

    this.props.handleMediaCollection(collection, false, this.scrollPosition);
  }

  /**
   * Merges collection changes with original, and submits to handler
   * @param  {Object} mediaCollection           Object containing uploaded media
   * @param  {Object} mediaUpdates              Object containing user changes
   * @return {Undefined} Returns nothing
   */
  handleSubmit(mediaCollection, mediaUpdates) {
    let collection = mediaCollection;

    if (!_.isEmpty(mediaUpdates)) {
      collection = _.cloneDeep(mediaCollection);
      _.map(mediaUpdates, modifiedItem => {
        const item = _.find(collection, {id: modifiedItem.id});

        if (item) {
          if (modifiedItem.remove) {
            _.remove(collection, obj => obj.id === modifiedItem.id);
          } else {
            item.caption = modifiedItem.caption;
          }
        }
      });
    }
    this.props.handleMediaCollection(collection, false, this.scrollPosition);
  }

  /**
   * Sets user changes to media items
   * @param  {Object} mediaUpdates        Object containing user changes
   * @param  {Object} newValues           Object containing possible id, caption and remove values to modify mediaUpdates object
   * @return {Undefined} Returns nothing
   */
  handleItemChange(mediaUpdates, newValues) {
    let obj = _.get(mediaUpdates, newValues.id);
    let removalCount = 0;

    if (_.isNil(obj)) {
      obj = mediaUpdates[newValues.id] = {};
    }
    obj.id = _.get(newValues, 'id');
    obj.caption = _.get(newValues, 'caption');
    obj.remove = _.get(newValues, 'remove');

    _.map(mediaUpdates, item => {
      if (item.remove) {
        removalCount++;
      }
    });

    this.setState({removalCount});
  }

  /**
   * Orientation change event handler
   * @return {Undefined} Returns nothing
   */
  handleOrientationChange() {
    setTimeout(() => this.setStyles(), 100);
  }

  /**
   * React Component render.
   * @return {JSX} CaptionContainer rendered.
   */
  render() {
    const {isInitialUpload, mediaCollection} = this.props;
    const collectionCount = this.collectionSize - this.state.removalCount;
    const mediaString =
      collectionCount === 1 ? this.mediaType : `${this.mediaType}s`;
    const {mobileDetails: {any: mobile} = {}} = this.props;

    return this.collectionSize === 0 ? null : (
      <div
        className={`${styles.CaptionContainer}${
          mobile ? ` ${styles.mobile}` : ''
        }`}
        style={mobile ? {height: this.state.windowHeight} : null}
      >
        <div className={styles.header}>
          <h2>Edit Media</h2>
          <p>{`${collectionCount} ${mediaString}`}</p>
        </div>
        <div
          className={styles.body}
          ref={this.setBodyRef}
          style={mobile ? {height: this.state.bodyHeight} : null}
        >
          {mediaCollection.map((item, index) => (
            <Item
              changeHandler={_.partial(
                this.handleItemChange,
                this.mediaUpdates
              )}
              height={this.state.itemHeight}
              key={index}
              media={item}
              mobile={mobile}
              ref={this.setItemRef}
            />
          ))}
        </div>
        <div
          className={styles.footer}
          ref={this.setFooterRef}
          style={mobile ? {top: this.state.footerTop} : null}
        >
          <Button
            btnType="primary"
            className={styles.cancel}
            handleClick={_.partial(
              this.handleCancel,
              mediaCollection,
              isInitialUpload
            )}
          >
            Cancel
          </Button>
          <Button
            btnType="primary"
            className={styles.submit}
            disabled={false}
            handleClick={_.partial(
              this.handleSubmit,
              mediaCollection,
              this.mediaUpdates
            )}
          >
            Save & Continue
          </Button>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/reactModules/pages/Person/components/GuestBook/CaptionContainer.js